import {
  applyAppearanceOverride,
  normalizeAppearanceOverrides,
} from "./appearance";
import {
  defaultAppearanceForHolding,
  holdingSeed,
  stableSeed,
} from "./identity";
import {
  calculateAmountRatio,
  calculateFarmUnit,
  calculateMood,
  calculateSlimeScale,
  deriveRoomMood,
  hasUsableReturn,
} from "./math";
import { MAX_STORED_PORTFOLIO_HOLDINGS } from "./portfolio/constants";
import { preferredHoldingDisplayName } from "./portfolio/identity";
import type {
  AppearanceOverrides,
  DeriveSceneOptions,
  HoldingSnapshot,
  PortfolioSnapshot,
  SceneModel,
  SlimeSpec,
} from "./types";

export const SCENE_ENGINE_VERSION = "0.1.0";
export const DEFAULT_MAX_SLIMES = MAX_STORED_PORTFOLIO_HOLDINGS;
const MIN_VISIBLE_SIZE_TRANSITION = 0.005;

function safeMarketValue(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 0;
}

function safeFarmUnit(portfolio: PortfolioSnapshot): number {
  return Number.isFinite(portfolio.farmUnit) && portfolio.farmUnit > 0
    ? portfolio.farmUnit
    : calculateFarmUnit(portfolio.positions);
}

function resolveOverride(
  holding: HoldingSnapshot,
  overrides: AppearanceOverrides,
) {
  return {
    ...overrides[holding.ticker],
    ...overrides[holding.instrumentId],
  };
}

function compareHoldings(left: HoldingSnapshot, right: HoldingSnapshot) {
  const valueDelta =
    safeMarketValue(right.marketValue) - safeMarketValue(left.marketValue);
  if (valueDelta !== 0) {
    return valueDelta;
  }

  return left.instrumentId.localeCompare(right.instrumentId);
}

function holdingDisplayName(holding: HoldingSnapshot): string {
  const projectedDisplayName = holding.displayName?.normalize("NFKC").trim();
  if (projectedDisplayName) {
    return projectedDisplayName;
  }

  if (holding.ticker.trim() === "?") {
    return holding.name.trim() || "?";
  }

  return preferredHoldingDisplayName(holding);
}

export function deriveSceneModel(
  portfolio: PortfolioSnapshot,
  rawOverrides: AppearanceOverrides = {},
  options: DeriveSceneOptions = {},
): SceneModel {
  const overrides = normalizeAppearanceOverrides(rawOverrides);
  const farmUnit = safeFarmUnit(portfolio);
  const { room, totalMarketValue, weightedDailyReturnPct } = deriveRoomMood(
    portfolio.positions,
  );
  const requestedMax = options.maxSlimes ?? DEFAULT_MAX_SLIMES;
  const maxSlimes = Math.max(0, Math.floor(requestedMax));
  const sorted = [...portfolio.positions].sort(compareHoldings);
  const visible = sorted.slice(0, maxSlimes);

  const slimes: SlimeSpec[] = visible.map((holding) => {
    const marketValue = safeMarketValue(holding.marketValue);
    const dailyReturnPct = hasUsableReturn(holding)
      ? holding.dailyReturnPct
      : null;
    const mood = calculateMood(dailyReturnPct);
    const appearance = applyAppearanceOverride(
      defaultAppearanceForHolding(holding),
      resolveOverride(holding, overrides),
    );

    return {
      id: holding.instrumentId,
      instrumentId: holding.instrumentId,
      ticker: holding.ticker,
      name: holding.name,
      displayName: appearance.nickname ?? holdingDisplayName(holding),
      marketValue,
      weight: totalMarketValue > 0 ? marketValue / totalMarketValue : 0,
      amountRatio: calculateAmountRatio(marketValue, farmUnit),
      dailyReturnPct,
      quoteStatus: holding.quoteStatus,
      seed: holdingSeed(holding),
      scale: calculateSlimeScale(marketValue, farmUnit),
      targetScale: calculateSlimeScale(marketValue, farmUnit),
      mood,
      joy: Math.max(mood, 0),
      gloom: Math.max(-mood, 0),
      characterSignals: holding.characterSignals,
      appearance,
    };
  });

  return {
    schemaVersion: 1,
    engineVersion: SCENE_ENGINE_VERSION,
    scenarioId: options.scenarioId ?? portfolio.portfolioId,
    sceneSeed: stableSeed(portfolio.portfolioId),
    portfolioId: portfolio.portfolioId,
    asOf: portfolio.asOf,
    marketDate: portfolio.marketDate,
    currency: portfolio.currency,
    farmUnit,
    totalMarketValue,
    weightedDailyReturnPct,
    slimes,
    hiddenCount: Math.max(0, sorted.length - visible.length),
    room,
  };
}

/**
 * Adds a one-shot visual edge between two observations without changing any
 * financial meaning in the current scene. In particular, daily return and
 * mood continue to describe the provider's previous-close comparison.
 */
export function withSceneSizeTransitions(
  currentScene: SceneModel,
  previousScene: SceneModel,
  transitionId: string,
): SceneModel {
  if (
    !transitionId ||
    currentScene.portfolioId !== previousScene.portfolioId
  ) {
    return currentScene;
  }

  const previousByInstrument = new Map(
    previousScene.slimes.map((slime) => [slime.instrumentId, slime]),
  );
  let changed = false;
  const slimes: SlimeSpec[] = currentScene.slimes.map((slime) => {
    const previous = previousByInstrument.get(slime.instrumentId);
    if (!previous) {
      return slime;
    }
    const fromScale = previous.targetScale;
    const toScale = slime.targetScale;
    if (
      !Number.isFinite(fromScale) ||
      !Number.isFinite(toScale) ||
      Math.abs(toScale - fromScale) < MIN_VISIBLE_SIZE_TRANSITION
    ) {
      return slime;
    }

    changed = true;
    const direction = toScale > fromScale ? "grow" : "shrink";
    return {
      ...slime,
      sizeTransition: {
        transitionId: `${transitionId}:${slime.instrumentId}`,
        fromScale,
        toScale,
        direction,
      },
    };
  });

  return changed ? { ...currentScene, slimes } : currentScene;
}
